/**
 * WishlistContext — localStorage-backed until a real backend endpoint lands.
 * Stores a Set of product IDs; component consumers get toggle/has/count.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const LS_KEY = "bb_wishlist_v1";
const WishlistCtx = createContext(null);

const read = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const write = (arr) => { try { localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch { /* noop */ } };

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState(read);

  // React to other tabs updating the wishlist.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === LS_KEY) setIds(read()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);
  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev];
      write(next);
      return next;
    });
  }, []);
  const remove = useCallback((id) => {
    setIds((prev) => { const next = prev.filter((x) => x !== id); write(next); return next; });
  }, []);
  const clear = useCallback(() => { setIds([]); write([]); }, []);

  const value = useMemo(() => ({ ids, count: ids.length, has, toggle, remove, clear }),
    [ids, has, toggle, remove, clear]);
  return <WishlistCtx.Provider value={value}>{children}</WishlistCtx.Provider>;
}

export const useWishlist = () => useContext(WishlistCtx) || { ids: [], count: 0, has: () => false, toggle: () => {}, remove: () => {}, clear: () => {} };
